import React from 'react'
import { observer } from 'mobx-react-lite'
import { useVirtualizer } from '@tanstack/react-virtual'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'

import { ANALYTICS_EVENTS, Loader, useEvent } from '../../../common'
import { type Transaction } from '../../../core'

import { useTransactionsUiStore } from '../../context/hooks'
import { useTransactionStore } from '../../../common'

import SubheaderRow from '../../../common/components/SubheaderRow'
import TransactionRow from './transactionlist/TransactionRow'
import ZeroState from './transactionlist/ZeroState'

import { hasInsetBorder, shouldShowDate } from '../../utils/Transactions'
import { fromUnixUTCToDayOfWeekAndDate } from '../../../core/utils/DateUtil'

const ROW_HEIGHT = 65
const DATE_ROW_HEIGHT = 32

export interface TransactionListProps {
  bgcolor?: string
  filter?: (t: Transaction) => boolean
  height?: number | string
  loader?: React.JSX.Element
  onClick?: (guid: string) => void
  showInsights?: boolean
  showLoader?: boolean
  transaction?: Transaction[]
  width?: number | string
  zeroState?: React.JSX.Element
}

const TransactionList: React.FC<TransactionListProps> = ({
  bgcolor,
  filter,
  height = 'calc(100dvh - 36px)',
  loader = <Loader label="Loading" size={40} />,
  onClick,
  showInsights = false,
  showLoader = false,
  transaction,
  width = '100%',
  zeroState = <ZeroState />,
}) => {
  const { isLoading, sortedTransactions: allTransactions } = useTransactionStore()
  const transactions = React.useMemo(() => {
    const updatedTransactions = filter ? allTransactions.filter(filter) : allTransactions
    return transaction ?? updatedTransactions
  }, [allTransactions, filter, transaction])

  const { clearExpandedSplits, setShowInsights, toggleSplit } = useTransactionsUiStore()
  const { onEvent } = useEvent()

  const handleClick = (guid: string) => {
    const transaction = transactions.find((t) => t.guid === guid)

    if (transaction?.has_been_split) {
      toggleSplit(transaction.guid)
      return
    }

    onEvent(ANALYTICS_EVENTS.ACCOUNT_DETAILS_CLICK_TRANSACTION, {
      account_guid: transaction?.account_guid,
      transaction_guid: guid,
    })
    onClick?.(guid)
  }

  const parentRef = React.useRef<HTMLDivElement>(null)
  const rowVirtualizer = useVirtualizer({
    count: transactions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => ROW_HEIGHT + (shouldShowDate(i, transactions) ? DATE_ROW_HEIGHT : 0),
    overscan: 5,
  })

  React.useEffect(() => rowVirtualizer.measure(), [transactions])
  React.useEffect(() => setShowInsights(showInsights), [showInsights])

  React.useEffect(() => {
    return () => clearExpandedSplits()
  }, [])

  return (
    <List component="div" role="list" sx={{ width: width }}>
      <Box height={height} overflow="auto" ref={parentRef} role="presentation" width={width}>
        <Box
          height={
            // set the height to auto when there are no transactions so the zero state isn't set to 0
            rowVirtualizer.getVirtualItems().length === 0 ? 'auto' : rowVirtualizer.getTotalSize()
          }
          position="relative"
          role="presentation"
          width="100%"
        >
          {(isLoading || showLoader) && loader}
          {!isLoading && !showLoader && rowVirtualizer.getVirtualItems().length === 0 && zeroState}
          {!isLoading &&
            !showLoader &&
            rowVirtualizer.getVirtualItems().length > 0 &&
            rowVirtualizer.getVirtualItems().map((row) => {
              const transaction = transactions[row.index]
              const showDate = shouldShowDate(row.index, transactions)
              const isInset = hasInsetBorder(row.index, transactions)
              const rowHeight = ROW_HEIGHT + (showDate ? DATE_ROW_HEIGHT : 0)

              return (
                <Box
                  key={transaction.guid}
                  role="listitem"
                  sx={{
                    height: rowHeight,
                    position: 'absolute',
                    transform: `translateY(${row.start}px)`,
                    width: '100%',
                    bgcolor: 'background.paper',
                  }}
                >
                  {showDate && (
                    <SubheaderRow bgcolor={bgcolor}>
                      {fromUnixUTCToDayOfWeekAndDate(transaction.date)}
                    </SubheaderRow>
                  )}
                  <TransactionRow
                    component="div"
                    {...(onClick && { onClick: handleClick })}
                    transaction={transaction}
                  />
                  <Divider sx={{ ml: isInset ? 68 : 0 }} />
                </Box>
              )
            })}
        </Box>
      </Box>
    </List>
  )
}

export default observer(TransactionList)
